import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import Panel from "common/NewLook/Panel.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { formatDate } from "@shared/utils/date";
import SupportedExchanges from "features/newMarketplace/GroupExtraDetails/Tabs/OverviewTab/SupportedExchanges";
import ContactInfo from "features/newMarketplace/GroupExtraDetails/Tabs/OverviewTab/ContactInfo";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import React, { useContext } from "react";
import Flex from "common/Boxes/Flex";
import OverviewStatistics from "features/newMarketplace/GroupExtraDetails/Tabs/OverviewTab/OverviewStatistics";
import { SubGroupDetailsContext } from "@shared/features/newMarketplace/SubGroupDetails.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

const OverviewTabContent = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { groupOverview } = useContext(SubGroupDetailsContext)!;
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();

  if (!groupOverview) return null;

  if (isNewLook) {
    return (
      <NewLookStack>
        <OverviewStatistics />
        <Panel title={t("overview.about")}>
          {!!groupOverview.group_description && (
            <NewLookBody>{groupOverview.group_description}</NewLookBody>
          )}
          <NewLookMeta>
            {t("overview.group_created_on")}{" "}
            {formatDate(groupOverview.creation_date, false, true)}
          </NewLookMeta>
        </Panel>
        <Panel
          title={t("overview.supported_exchanges")}
          extra={
            <NewLookMeta as="span">
              {t("overview.signals_last_month")}
            </NewLookMeta>
          }
          flush={true}
        >
          <SupportedExchanges
            supportedExchanges={groupOverview.supported_exchanges_info}
          />
        </Panel>
        {!!Object.keys(groupOverview.contact_info || {}).length && (
          <Panel title={t("overview.contact_info")}>
            <ContactInfo contactInfo={groupOverview.contact_info} />
          </Panel>
        )}
      </NewLookStack>
    );
  }

  return (
    <>
      <OverviewStatistics />
      <Wrapper>
        <Title level={3} style={{ marginBottom: 12 }}>
          {t("overview.about")}
        </Title>
        <Text type="medium">{groupOverview.group_description}</Text>
        <CreationDate $isMobile={isMobile}>
          {t("overview.group_created_on")}{" "}
          {formatDate(groupOverview.creation_date, false, true)}
        </CreationDate>
      </Wrapper>
      <Wrapper>
        <Flex $justifyContent="flex-start" style={{ marginBottom: 20 }}>
          <Title level={3}>{t("overview.supported_exchanges")}</Title>
          <Title level={4} type="secondary" style={{ marginLeft: 4 }}>
            {t("overview.signals_last_month")}
          </Title>
        </Flex>
        <SupportedExchanges
          supportedExchanges={groupOverview.supported_exchanges_info}
        />
      </Wrapper>
      <Wrapper $isLast={true}>
        <Title level={3} style={{ marginBottom: 20 }}>
          {t("overview.contact_info")}
        </Title>
        <ContactInfo contactInfo={groupOverview.contact_info} />
      </Wrapper>
    </>
  );
};

export default OverviewTabContent;

const Wrapper = styled.div<{ $isLast?: boolean }>`
  padding: 24px 0;
  ${(p) =>
    !p.$isLast &&
    `
    border-bottom: ${p.theme.borders.light};
  `};
`;
const NewLookStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
  text-align: left;
`;
const NewLookBody = styled.p`
  margin: 0 0 8px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: pre-line;
  overflow-wrap: anywhere;
`;
const NewLookMeta = styled.p`
  margin: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const CreationDate = styled(Text)<{ $isMobile?: boolean }>`
  color: ${(p) => p.theme.colors.secondaryText};
  margin-top: 20px;
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;
